// Typed API client.
// Wraps every Stratus endpoint we've shipped. Tokens come from localStorage
// (set by Login + OIDC callback). Expired/rejected tokens auto-redirect to
// /login with ?expired=1&from=<path> so the user never sees a silent blank UI.

import { getActiveTenant, shouldScope, ALL } from './tenantScope';
import { withQuery, filenameFromDisposition } from './portalUx';

const TOKEN_KEY = 'stratus_token';

// Clock skew tolerance — treat token as expired this many seconds before its
// real exp, so we kick the user before the server starts rejecting requests.
const EXP_SKEW_SECONDS = 5;

type JwtPayload = { exp?: number; iat?: number; [k: string]: any };

function decodeJwt(token: string): JwtPayload | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((part.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch { return null; }
}

function isExpired(token: string): boolean {
  const p = decodeJwt(token);
  if (!p || typeof p.exp !== 'number') return false; // no exp claim → trust until server says no
  return (Date.now() / 1000) >= (p.exp - EXP_SKEW_SECONDS);
}

let expiryTimer: number | null = null;
function scheduleExpiryWatchdog(token: string | null) {
  if (expiryTimer !== null) { clearTimeout(expiryTimer); expiryTimer = null; }
  if (!token) return;
  const p = decodeJwt(token);
  if (!p || typeof p.exp !== 'number') return;
  const msUntilExpiry = (p.exp - EXP_SKEW_SECONDS) * 1000 - Date.now();
  // Fire even if already past exp (msUntilExpiry <= 0) — let the redirect handle it.
  // setTimeout's delay is a 32-bit signed int. A value > 2^31-1 ms (~24.8 days)
  // overflows and fires immediately. Clamp + re-check + re-schedule.
  const MAX_TIMER_MS = 2_000_000_000;
  const delay = Math.max(0, Math.min(msUntilExpiry, MAX_TIMER_MS));
  expiryTimer = window.setTimeout(() => {
    expiryTimer = null;
    // The clamp may have woken us early — verify the token is actually expired
    // before logging the user out.
    const cur = localStorage.getItem(TOKEN_KEY);
    if (cur && !isExpired(cur)) {
      scheduleExpiryWatchdog(cur);
      return;
    }
    if (!window.location.pathname.startsWith('/login')) {
      logoutAndRedirect('expired');
    } else {
      setToken(null); // strip stale token even on /login, to keep state clean
    }
  }, delay);
}

export function getToken(): string | null {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return null;
  if (isExpired(t)) return null;
  return t;
}

// Returns the raw token even if expired. Use only when sending to a server that
// will validate (e.g. logout endpoint) — never for normal requests.
export function getRawToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string | null) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
  scheduleExpiryWatchdog(t);
}

// Centralized logout + redirect. Replaces (not pushes) so the user can't
// "back" into a page that's about to fail. Preserves where they were so we
// can send them back after re-auth.
export type LogoutReason = 'expired' | 'rejected' | 'manual';
export function logoutAndRedirect(reason: LogoutReason = 'manual') {
  if (expiryTimer !== null) { clearTimeout(expiryTimer); expiryTimer = null; }
  localStorage.removeItem(TOKEN_KEY);
  if (window.location.pathname.startsWith('/login')) return; // already there
  const from = window.location.pathname + window.location.search;
  const qp = new URLSearchParams();
  if (reason === 'expired' || reason === 'rejected') qp.set('expired', '1');
  if (from && from !== '/') qp.set('from', from);
  const qs = qp.toString();
  window.location.replace('/login' + (qs ? '?' + qs : ''));
}

// Initialize the watchdog on module load (covers refresh-with-valid-token case).
scheduleExpiryWatchdog(localStorage.getItem(TOKEN_KEY));

// Cross-tab logout: if another tab clears the token, react in this tab too.
window.addEventListener('storage', (e) => {
  if (e.key === TOKEN_KEY && e.newValue === null) {
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace('/login');
    }
  }
});

export class AuthRejectedError extends Error {
  constructor() { super('auth rejected'); this.name = 'AuthRejectedError'; }
}

// Single in-flight refresh promise — multiple concurrent 401s share one
// /api/auth/refresh round-trip instead of all racing.
let _refreshInflight: Promise<string | null> | null = null;
async function tryRefreshToken(): Promise<string | null> {
  if (_refreshInflight) return _refreshInflight;
  // Use the *raw* token (even expired) — the server will accept tokens within
  // a short grace window past exp, and validate signature regardless.
  const raw = getRawToken();
  if (!raw) return null;
  _refreshInflight = (async () => {
    try {
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${raw}` },
      });
      if (!r.ok) return null;
      const j = await r.json();
      if (j?.token) { setToken(j.token); return j.token as string; }
      return null;
    } catch {
      return null;
    } finally {
      _refreshInflight = null;
    }
  })();
  return _refreshInflight;
}

// Called on app load: re-mint the token from the server so an admin's role change (promote/demote)
// takes effect without a full re-login. The refresh endpoint re-reads the live console_role, so if
// this user's role actually changed since their last token, reload once — the nav and API guards
// read app_role from the token, so they only pick up the new role after a re-render. (#466)
export async function syncIdentity(): Promise<void> {
  const raw = getRawToken();
  if (!raw) return;
  const before = decodeJwt(raw)?.app_role ?? null;
  const t = await tryRefreshToken();
  if (!t) return;
  const after = decodeJwt(t)?.app_role ?? null;
  if (after !== before) window.location.reload();
}

// Department view-scope: when a department is selected in the top bar, inject
// ?domain_uuid=<uuid> onto GETs so the existing pages render scoped. View only;
// non-GET and excluded paths (auth, tenant list, etc.) pass through untouched.
function applyScope(method: string, path: string): string {
  if ((method || 'GET').toUpperCase() !== 'GET') return path;
  const active = getActiveTenant();
  if (active === ALL || !shouldScope(path)) return path;
  try {
    const u = new URL(path, window.location.origin);
    u.searchParams.set('domain_uuid', active);
    return u.pathname + u.search;
  } catch { return path; }
}

// For POST/PUT bodies (which applyScope's GET-only injection doesn't reach):
// the selected department, or {} when none is active. Lets an admin act on a
// chosen client; a normal user has no active tenant so this is always {}.
function scopedBody(): { domain_uuid?: string } {
  const active = getActiveTenant();
  return active && active !== ALL ? { domain_uuid: active } : {};
}

async function req<T = any>(method: string, path: string, body?: any, opts: RequestInit = {}): Promise<T> {
  const token = getToken(); // returns null if expired — server will 401, we'll redirect
  const url = applyScope(method, path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((opts.headers as Record<string, string>) || {}),
  };
  const r = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...opts,
  });
  if (r.status === 401) {
    // Don't try to refresh on the auth endpoints themselves.
    const isAuthPath = path.startsWith('/api/auth/');
    if (!isAuthPath) {
      // First 401: try one silent refresh + retry. If refresh fails, logout.
      const newTok = await tryRefreshToken();
      if (newTok) {
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${newTok}`,
        };
        const r2 = await fetch(url, {
          method,
          headers: retryHeaders,
          body: body !== undefined ? JSON.stringify(body) : undefined,
          ...opts,
        });
        if (r2.ok) {
          const ct = r2.headers.get('content-type') || '';
          return ct.includes('application/json') ? r2.json() : (r2.text() as any);
        }
        // Retry also failed — fall through to logout.
      }
      logoutAndRedirect('rejected');
    }
    throw new AuthRejectedError();
  }
  if (!r.ok) {
    const text = await r.text();
    throw new Error(`${r.status}: ${text || r.statusText}`);
  }
  const ct = r.headers.get('content-type') || '';
  return ct.includes('application/json') ? r.json() : (r.text() as any);
}

// Auth-aware fetch for callers that need raw Response (file upload, streaming,
// non-JSON). Attaches the bearer and handles 401 the same way as req().
export async function apiFetch(path: string, opts: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((opts.headers as Record<string, string>) || {}),
  };
  const url = applyScope(opts.method || 'GET', path);
  const r = await fetch(url, { ...opts, headers });
  if (r.status === 401 && !path.startsWith('/api/auth/login')) {
    logoutAndRedirect('rejected');
    throw new AuthRejectedError();
  }
  return r;
}

// Download a file with the bearer in the Authorization header instead of a ?token= URL
// (tokens in URLs leak into server/proxy logs and browser history). Saves via a blob link.
export async function downloadWithAuth(path: string, fallbackName: string): Promise<void> {
  const r = await apiFetch(path);
  if (!r.ok) throw new Error(`${r.status}: ${await r.text().catch(() => r.statusText)}`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filenameFromDisposition(r.headers.get('content-disposition'), fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Open an authenticated media/file URL in a new tab without putting the token in the URL. The
// tab is opened synchronously (so popup blockers allow it), then pointed at a blob URL.
export async function openWithAuth(path: string): Promise<void> {
  const w = window.open('', '_blank');
  try {
    const r = await apiFetch(path);
    if (!r.ok) throw new Error(`${r.status}: ${await r.text().catch(() => r.statusText)}`);
    const url = URL.createObjectURL(await r.blob());
    if (w) w.location.href = url; else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 10 * 60_000);
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

// ── personal + shared contacts (backed by /api/me/contacts) ────────────────
// `api.me` is already the JWT decoder above, so this ships as `api.contacts`.
export type MyContact = {
  contact_uuid: string; name: string; number: string;
  email: string | null; company: string | null; title: string | null;
  source: string | null; source_id: string | null; updated_at: string;
};
export type ContactGroup = { group_uuid: string; name: string; role: string };
export type SharedContact = {
  share_uuid: string; group_uuid: string; group_name: string; shared_by: string;
  is_owner: boolean; contact_uuid: string; name: string; number: string;
  email: string | null; company: string | null; title: string | null;
};
export type ContactShare = { group_uuid: string; group_name: string };
export type ContactInput = {
  name: string; number: string;
  email?: string; company?: string; title?: string;
  source?: string; source_id?: string;
};

// Ad-hoc iOS beta portal /admin roster (proxied via /api/beta-testers).
export type BetaTester = {
  email: string; udid: string | null; state: string;
  buildId: string | null; createdAt: string;
};
export type BetaPortalStatus = {
  slots: { used: number; cap: number; remaining: number };
  devices: { udid: string; name: string; class: string; enabled: boolean }[];
  testers: BetaTester[];
  latestBuild: { id: string; appVersion?: string; ipaFile?: string; udids?: string[]; finishedAt?: string } | null;
  worker: { building: boolean; rebuildQueued: boolean };
};

// Operator Square billing (#1050). Amounts are dollars unless suffixed _cents.
export type BillingTenant = {
  domain_uuid: string; domain_name: string; domain_description: string | null;
  domain_enabled: string; desk_seats: number; mobile_seats: number; monthly_total: number;
  square_customer_linked: boolean;
  last_invoice: {
    status: string; dry_run: boolean; period_start: string; period_end: string;
    amount_cents: number; invoice_url: string | null; created_at: string;
  } | null;
};
export type BillingSummary = {
  tenants: BillingTenant[];
  totals: { desk_seats: number; mobile_seats: number; monthly_total: number };
};
export type BillingInvoice = {
  invoice_uuid: string; square_invoice_id: string; square_order_id: string;
  period_start: string; period_end: string; desk_seats: number; mobile_seats: number;
  amount_cents: number; monthly_total: string | number; status: string; dry_run: boolean;
  invoice_url: string | null; created_at: string; created_by: string;
};

export const api = {
  // ── auth ────────────────────────────────────────────────────────────────
  // Login is two-step when 2FA applies: a first response may carry no session
  // token but instead {mfa:'verify'|'enroll', mfa_token, ...}; the caller then
  // POSTs the code to mfaVerify to obtain the real token.
  login: (username: string, password: string, turnstile?: string) =>
    req<{ token?: string; user?: any; mfa?: 'verify' | 'enroll'; mfa_token?: string; otpauth_url?: string; secret?: string; qr_data_url?: string }>(
      'POST', '/api/auth/login', { username, password, turnstile }),
  // verify accepts a 6-digit TOTP or a 10-char one-time backup code (#941);
  // backup_codes rides back exactly once, on enrollment confirmation.
  mfaVerify: (mfa_token: string, code: string) =>
    req<{ token: string; user: any; backup_codes?: string[] }>('POST', '/api/auth/mfa/verify', { mfa_token, code }),
  // Self-service: mint a fresh set of 10 one-time backup codes (old set dies).
  mfaBackupCodes: () => req<{ codes: string[] }>('POST', '/api/auth/mfa/backup-codes'),
  logout: () => req('POST', '/api/auth/logout'),
  me: () => {
    const t = getToken(); if (!t) return null;
    try { return JSON.parse(atob(t.split('.')[1])); } catch { return null; }
  },

  // ── releases / updates (versioning + on-demand deploy) ───────────────────
  releases: {
    list: () => req<{
      versions: { version: string; date: string; sha: string; headline: string }[];
      current: string; latest: string | null; unreleased_count: number; unreleased: string[];
      deployed_sha: string | null; pending: string | null; can_prep?: boolean;
    }>('GET', '/api/releases'),
    prep: (type: 'release' | 'hotfix' | 'major', notes?: string) =>
      req<{ ok: boolean; version: string; type: string; changelog: string }>('POST', '/api/releases/prep', { type, notes }),
    deploy: (version: string) =>
      req<{ ok: boolean; requested: string; note: string }>('POST', '/api/releases/deploy', { version }),
  },

  // ── tenants / departments ───────────────────────────────────────────────
  tenants: {
    list: () => req<any[]>('GET', '/api/tenants'),
    get: (id: string) => req('GET', `/api/tenants/${id}`),
    create: (body: any) => req('POST', '/api/tenants', body),
    setRealm: (id: string, domain_name: string) => req('POST', `/api/tenants/${id}/realm`, { domain_name }),
    deleteVerified: (id: string, password: string) => req('POST', `/api/tenants/${id}/delete`, { password }),
    update: (id: string, body: any) => req('PUT', `/api/tenants/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/tenants/${id}`),
    stats: (id: string) => req('GET', `/api/tenants/${id}/stats`),
    dashboard: (id: string) => req('GET', `/api/tenants/${id}/dashboard`),
  },

  // ── users / extensions ──────────────────────────────────────────────────
  users: {
    list: () => req<any[]>('GET', '/api/users'),
    get: (id: string) => req('GET', `/api/users/${id}`),
    create: (body: any) => req('POST', '/api/users', body),
    update: (id: string, body: any) => req('PUT', `/api/users/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/users/${id}`),
    nextExtension: (domain: string) => req<{ next: string }>('GET', `/api/users/next-extension?domain_uuid=${domain}`),
    resetPassword: (id: string) => req<{ ok: boolean; password: string }>('POST', `/api/users/${id}/reset-password`),
    clone: (id: string, body: { new_extension?: string } = {}) =>
      req<{ ok: boolean; extension_uuid: string; extension: string; sip_password: string }>('POST', `/api/users/${id}/clone`, body),
    bulk2fa: (ids: string[], enable: boolean) =>
      req<{ ok: boolean; touched: number; enabled: boolean }>('POST', '/api/users/bulk-2fa', { ids, enable }),
    importCsv: async (csv: string) => {
      const r = await fetch('/api/users/import', {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv', Authorization: `Bearer ${getToken()}` },
        body: csv,
      });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ ok: boolean; created: number; skipped: number; failed: number; details: any }>;
    },
  },

  // ── phones / devices ────────────────────────────────────────────────────
  devices: {
    list: (tid: string) => req<any[]>('GET', `/api/tenants/${tid}/devices`),
    create: (tid: string, body: any) => req('POST', `/api/tenants/${tid}/devices`, body),
    update: (tid: string, id: string, body: any) => req('PUT', `/api/tenants/${tid}/devices/${id}`, body),
    remove: (tid: string, id: string) => req('DELETE', `/api/tenants/${tid}/devices/${id}`),
    reprovision: (tid: string, id: string) =>
      req<{ ok: boolean; via?: string; request_id?: number | null; message?: string }>('POST', `/api/tenants/${tid}/devices/${id}/reprovision`),
    // Read-only department views (portal-phones-ux): labelled dial codes + parking orbits, and the
    // recording master switch + per-user recording flags.
    featureCodes: (tid: string) => req<{
      codes: { key: string; label: string; code: string; usage: string[] }[];
      parking: { spots: string[]; ranges: string[]; usage: string[] };
    }>('GET', `/api/tenants/${tid}/feature-codes`),
    recordingStatus: (tid: string) => req<{
      domain_uuid: string; department_recording_enabled: boolean;
      users: Record<string, { record_calls: boolean; record_external_only: boolean }>;
    }>('GET', `/api/tenants/${tid}/recording-status`),
    // DECT handset slots (#799): a DP750 base hosts up to 10 handsets, each its own user.
    handsets: (tid: string, id: string) => req<{
      device_uuid: string; mac: string; model: string; max: number; used: number;
      slots: { slot: number; extension: string | null; extension_uuid: string | null; name: string | null; display_name: string | null }[];
    }>('GET', `/api/tenants/${tid}/devices/${id}/handsets`),
    addHandset: (tid: string, id: string, extension_uuid: string) =>
      req<{ ok: boolean; slot: number; extension: string }>('POST', `/api/tenants/${tid}/devices/${id}/handsets`, { extension_uuid }),
    removeHandset: (tid: string, id: string, slot: number) =>
      req('DELETE', `/api/tenants/${tid}/devices/${id}/handsets/${slot}`),
    discovered: (params: { status?: string; sbc_id?: string } = {}) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/phones/discovered${qs ? '?' + qs : ''}`);
    },
    ignoreDiscovered: (id: string) => req('DELETE', `/api/phones/discovered/${id}`),
    scan: (sbc_node_id: string = 'all') => req('POST', '/api/phones/scan', { sbc_node_id }),
    assign: (body: { discovered_phone_id: string; extension_uuid: string; domain_uuid: string; label?: string; model?: string; sn?: string; remote?: boolean; rpsProfile?: string }) =>
      req('POST', '/api/phones/assign', body),
    reboot: (device_uuid: string) => req('POST', `/api/phones/${device_uuid}/reboot`),

    // ── GDMS/RPS enrollment for zero-touch Grandstream (#642, backend #634) ──
    // The per-site RPS profile picker's options + whether the remote (public-HTTPS)
    // profile is actually configured.
    rpsProfiles: () => req<{
      remoteReady: boolean; localProfile: string | null; remoteProfile: string | null;
      profiles: { key: string; label: string; profile: string | null; localOnly: boolean; ready: boolean; source: string; warning?: string | null; hint?: string }[];
    }>('GET', '/api/phones/rps-profiles'),
    // Single-device SN capture + GDMS/RPS enroll (threaded from the Assign dialog).
    enroll: (body: { mac: string; sn: string; domain_uuid?: string; site?: string; vendor_key?: string; remote?: boolean; rpsProfile?: string }) =>
      req<{ ok: boolean; mac: string; domain?: string; upstream: any; rps_uuid?: string }>('POST', '/api/phones/enroll', body),
    // Bulk CSV/paste importer — per-row success/failure for ~90-site batches.
    bulkEnroll: (body: {
      rows: { mac: string; sn: string; site?: string; domain_uuid?: string; extension?: string; label?: string; remote?: boolean; rpsProfile?: string }[];
      rpsProfile?: string; remote?: boolean;
    }) => req<{
      ok: boolean; total: number; enrolled: number; failed: number;
      results: { row: number; mac: string; sn: string; site?: string | null; extension?: string | null; label?: string | null; ok: boolean; status: string; domain?: string; error?: string | null; warning?: string | null; rps_uuid?: string }[];
    }>('POST', '/api/phones/bulk-enroll', body),
  },

  // ── trunks ──────────────────────────────────────────────────────────────
  trunks: {
    list: () => req<any[]>('GET', '/api/trunks'),
    get: (id: string) => req('GET', `/api/trunks/${id}`),
    create: (body: any) => req('POST', '/api/trunks', body),
    update: (id: string, body: any) => req('PUT', `/api/trunks/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/trunks/${id}`),
    status: (id: string) => req('GET', `/api/trunks/${id}/status`),
  },

  // ── sbc nodes (remote-office gateways) ────────────────────────────────────
  sbc: {
    nodes: () => req<any[]>('GET', '/api/sbc/nodes'),
    assignTenant: (id: string, domain_uuid: string) => req('POST', `/api/sbc/nodes/${id}/assign-tenant`, { domain_uuid }),
  },

  // ── outbound rules ──────────────────────────────────────────────────────
  outboundRules: {
    list: (domainUuid: string) => req<any[]>('GET', `/api/outbound-rules/${domainUuid}`),
    create: (domainUuid: string, body: any) => req('POST', `/api/outbound-rules/${domainUuid}`, body),
    update: (domainUuid: string, id: string, body: any) => req('PUT', `/api/outbound-rules/${domainUuid}/${id}`, body),
    remove: (domainUuid: string, id: string) => req('DELETE', `/api/outbound-rules/${domainUuid}/${id}`),
    move: (domainUuid: string, id: string, direction: 'up' | 'down') => req('POST', `/api/outbound-rules/${domainUuid}/${id}/move`, { direction }),
    reorder: (domainUuid: string, order: string[]) => req('POST', `/api/outbound-rules/${domainUuid}/reorder`, { order }),
  },

  // ── shared / group voicemail boxes (Phase 2) ───────────────────────────
  // A voicemail box decoupled from a single user: its own dialable mailbox
  // number, PIN, email(s), five greeting slots (custom_prompts prompt_uuids),
  // and a members list. domain_uuid is passed on the query/body; the API pins
  // it to the caller's tenant for non-super users. members[] may be sent inline
  // on create/update, or replaced via setMembers.
  voicemailBoxes: {
    list:   (domainUuid: string) => req<any[]>('GET', `/api/voicemail-boxes?domain_uuid=${encodeURIComponent(domainUuid)}`),
    get:    (boxUuid: string) => req<any>('GET', `/api/voicemail-boxes/${boxUuid}`),
    create: (domainUuid: string, body: any) => req<any>('POST', '/api/voicemail-boxes', { ...body, domain_uuid: domainUuid }),
    update: (boxUuid: string, body: any) => req<any>('PUT', `/api/voicemail-boxes/${boxUuid}`, body),
    remove: (boxUuid: string) => req('DELETE', `/api/voicemail-boxes/${boxUuid}`),
    getMembers: (boxUuid: string) => req<string[]>('GET', `/api/voicemail-boxes/${boxUuid}/members`),
    setMembers: (boxUuid: string, members: string[]) => req<{ add: string[]; remove: string[]; members: string[] }>('PUT', `/api/voicemail-boxes/${boxUuid}/members`, { members }),
  },

  // ── outbound rules v2 (parent/child, 1-5 routes per rule) ──────────────
  outboundRulesV2: {
    list: (domainUuid: string) => req<any[]>('GET', `/api/outbound-rules-v2?domain_uuid=${domainUuid}`),
    get: (ruleUuid: string) => req('GET', `/api/outbound-rules-v2/${ruleUuid}`),
    create: (body: any) => req('POST', '/api/outbound-rules-v2', body),
    wizard: (body: { domain_uuid: string; gateway_uuid: string; area_code: string; caller_id: string; include_911?: boolean }) =>
      req('POST', '/api/outbound-rules-v2/wizard', body),
    update: (ruleUuid: string, body: any) => req('PUT', `/api/outbound-rules-v2/${ruleUuid}`, body),
    remove: (ruleUuid: string) => req('DELETE', `/api/outbound-rules-v2/${ruleUuid}`),
    move: (ruleUuid: string, direction: 'up' | 'down') => req('POST', `/api/outbound-rules-v2/${ruleUuid}/move`, { direction }),
    testRoute: (params: { number: string; domain_uuid: string; from_extension?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req('GET', `/api/outbound-rules-v2/test-route?${qs}`);
    },
  },

  // ── call legs (leg-tree CDR) ───────────────────────────────────────────
  callLegs: {
    list: (params: { domain_uuid: string; from_date?: string; to_date?: string; answered?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/call-legs?${qs}`);
    },
    get: (clCallId: number) => req('GET', `/api/call-legs/${clCallId}`),
    tree: (clCallId: number) => req('GET', `/api/call-legs/${clCallId}/tree`),
    byUuid: (fsUuid: string) => req('GET', `/api/call-legs/by-uuid/${fsUuid}`),
    flat: (params: { domain_uuid: string; from_date?: string; to_date?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/call-legs/view/flat?${qs}`);
    },
    searchByNumber: (params: { domain_uuid: string; number: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/call-legs/search/by-number?${qs}`);
    },
    qualityStats: (params: { domain_uuid: string; from_date?: string; to_date?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req('GET', `/api/call-legs/stats/quality?${qs}`);
    },
  },

  // ── wake-up calls (Hotel Lite) ──────────────────────────────────────────
  wakeupCalls: {
    list: (params: { domain_uuid: string; extension_uuid?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/wakeup-calls?${qs}`);
    },
    create: (body: any) => req('POST', '/api/wakeup-calls', body),
    update: (id: string, body: any) => req('PUT', `/api/wakeup-calls/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/wakeup-calls/${id}`),
    cancel: (id: string) => req('POST', `/api/wakeup-calls/${id}/cancel`),
  },

  // ── room housekeeping status (Hotel Lite, *68 MAID) ─────────────────────
  roomStatus: {
    board: (domain_uuid: string) => req<any[]>('GET', `/api/room-status?domain_uuid=${domain_uuid}`),
    codes: () => req<Record<string, string>>('GET', '/api/room-status/codes'),
    set: (extension_uuid: string, body: { domain_uuid: string; status_code: string }) =>
      req('PUT', `/api/room-status/${extension_uuid}`, body),
  },

  // ── LDAP/AD contact directory sync (company phonebook from AD) ──────────
  ldapSync: {
    get: (domain_uuid: string) => req<any>('GET', `/api/ldap-sync?domain_uuid=${domain_uuid}`),
    save: (body: any) => req<any>('PUT', '/api/ldap-sync', body),
    test: (body: any) => req<any>('POST', '/api/ldap-sync/test', body),
    syncNow: (domain_uuid: string) => req<any>('POST', '/api/ldap-sync/sync-now', { domain_uuid }),
    status: (domain_uuid: string) => req<any>('GET', `/api/ldap-sync/status?domain_uuid=${domain_uuid}`),
  },

  // ── public Talk URLs ────────────────────────────────────────────────────
  websiteLinks: {
    list: (domainUuid: string) => req<any[]>('GET', `/api/website-links?domain_uuid=${domainUuid}`),
    get: (id: string) => req('GET', `/api/website-links/${id}`),
    create: (body: any) => req('POST', '/api/website-links', body),
    update: (id: string, body: any) => req('PUT', `/api/website-links/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/website-links/${id}`),
    rotateSlug: (id: string) => req('POST', `/api/website-links/${id}/rotate-slug`),
    mintToken: (id: string, ttl_seconds = 600) => req('POST', `/api/website-links/${id}/mint-token`, { ttl_seconds }),
  },

  // ── voicemail metadata (sentiment/summary/transcription) ────────────────
  voicemailMetadata: {
    list: (params: { domain_uuid: string; callee?: string; from_date?: string; to_date?: string; heard?: string; archived?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/voicemail-metadata?${qs}`);
    },
    get: (id: string) => req('GET', `/api/voicemail-metadata/${id}`),
    markHeard: (id: string, by?: string) => req('POST', `/api/voicemail-metadata/${id}/heard`, { by }),
    archive: (id: string) => req('POST', `/api/voicemail-metadata/${id}/archive`),
    remove: (id: string) => req('DELETE', `/api/voicemail-metadata/${id}`),
  },

  // ── push registrations (mobile/web push tokens) ─────────────────────────
  pushRegistrations: {
    list: (params: { domain_uuid: string; extension_uuid?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/push-registrations?${qs}`);
    },
    register: (body: any) => req('POST', '/api/push-registrations', body),
    update: (id: string, body: any) => req('PUT', `/api/push-registrations/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/push-registrations/${id}`),
    removeAllForExt: (extId: string) => req('DELETE', `/api/push-registrations/by-extension/${extId}`),
  },

  // ── ad-hoc iOS beta portal monitor (super_admin / reseller) ──────────────
  betaTesters: {
    status: () => req<BetaPortalStatus>('GET', '/api/beta-testers'),
  },

  // ── firmware history (per-device + at-risk view) ────────────────────────
  firmwareHistory: {
    list: (params: { domain_uuid: string; mac_address?: string; vendor_key?: string; since?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/firmware-history?${qs}`);
    },
    forDevice: (device_uuid: string) => req<any[]>('GET', `/api/firmware-history/device/${device_uuid}`),
    atRisk: (params: { domain_uuid: string; severity?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/firmware-history/at-risk?${qs}`);
    },
    byVendor: (domain_uuid: string) => req<any[]>('GET', `/api/firmware-history/stats/by-vendor?domain_uuid=${domain_uuid}`),
  },

  // ── admin notifications (cert expiry, advisories, reboot fails) ─────────
  adminNotifications: {
    list: (params: { domain_uuid?: string; status?: string; severity?: string; category?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/admin-notifications?${qs}`);
    },
    counts: (domain_uuid?: string) => {
      const qs = domain_uuid ? `?domain_uuid=${domain_uuid}` : '';
      return req<{ info: number; warn: number; error: number; critical: number; total: number }>('GET', `/api/admin-notifications/counts${qs}`);
    },
    acknowledge: (id: number) => req('POST', `/api/admin-notifications/${id}/acknowledge`),
    dismiss: (id: number) => req('POST', `/api/admin-notifications/${id}/dismiss`),
    acknowledgeAll: (body: { domain_uuid?: string; category?: string }) => req('POST', '/api/admin-notifications/acknowledge-all', body),
  },

  // ── dashboard (full system snapshot) ─────────────────────────────────────
  dashboardFull: () => req('GET', '/api/dashboard/full'),

  // ── live system stats (CPU/RAM/network) + latency probes ────────────────
  liveStats: () => req<any>('GET', '/api/troubleshoot/live-stats'),
  networkLatency: () => req<any>('GET', '/api/troubleshoot/network-latency'),

  // ── advanced + system feature surfaces (Migration 019) ────────────────────
  advanced: {
    ipBlacklistList: () => req<any[]>('GET', '/api/advanced/ip-blacklist'),
    ipBlacklistAdd: (b: any) => req('POST', '/api/advanced/ip-blacklist', b),
    ipBlacklistUpdate: (id: string, b: any) => req('PUT', `/api/advanced/ip-blacklist/${id}`, b),
    ipBlacklistRemove: (id: string) => req('DELETE', `/api/advanced/ip-blacklist/${id}`),

    antiHackingGet: () => req('GET', '/api/advanced/anti-hacking'),
    antiHackingSet: (b: any) => req('PUT', '/api/advanced/anti-hacking', b),
    antiHackingBlocked: () => req('GET', '/api/advanced/anti-hacking/blocked'),

    consoleList: () => req<any[]>('GET', '/api/advanced/console-restrictions'),
    consoleAdd: (b: any) => req('POST', '/api/advanced/console-restrictions', b),
    consoleRemove: (id: string) => req('DELETE', `/api/advanced/console-restrictions/${id}`),

    networkGet: () => req('GET', '/api/advanced/network'),
    networkSet: (b: any) => req('PUT', '/api/advanced/network', b),

    countryList: () => req<any[]>('GET', '/api/advanced/country-codes'),
    countryAdd: (b: any) => req('POST', '/api/advanced/country-codes', b),
    countryRemove: (id: string) => req('DELETE', `/api/advanced/country-codes/${id}`),

    cidList: () => req<any[]>('GET', '/api/advanced/cid-rules'),
    cidAdd: (b: any) => req('POST', '/api/advanced/cid-rules', b),
    cidUpdate: (id: string, b: any) => req('PUT', `/api/advanced/cid-rules/${id}`, b),
    cidRemove: (id: string) => req('DELETE', `/api/advanced/cid-rules/${id}`),
    cidTest: (b: any) => req('POST', '/api/advanced/cid-rules/test', b),

    codecsGet: () => req('GET', '/api/advanced/codecs'),
    codecsSet: (b: any) => req('PUT', '/api/advanced/codecs', b),

    xmlList: () => req<any[]>('GET', '/api/advanced/xml-templates'),
    xmlAdd: (b: any) => req('POST', '/api/advanced/xml-templates', b),
    xmlUpdate: (id: string, b: any) => req('PUT', `/api/advanced/xml-templates/${id}`, b),
    xmlRemove: (id: string) => req('DELETE', `/api/advanced/xml-templates/${id}`),

    e164Get: () => req('GET', '/api/advanced/e164'),
    e164Set: (b: any) => req('PUT', '/api/advanced/e164', b),

    kvList: (ns: string) => req<any[]>('GET', `/api/advanced/kv/${ns}`),
    kvSet: (ns: string, key: string, b: any) => req('PUT', `/api/advanced/kv/${ns}/${key}`, b),
    kvRemove: (ns: string, key: string) => req('DELETE', `/api/advanced/kv/${ns}/${key}`),

    licenseGet: () => req('GET', '/api/advanced/license'),
    licenseFull: () => req<any>('GET', '/api/license'),
    licenseApply: (b: { key: string; licensed_to?: string }) => req<any>('PUT', '/api/license', b),
    updatesGet: () => req('GET', '/api/advanced/updates'),
    updatesCheck: () => req('POST', '/api/advanced/updates/check'),
  },

  // ── onboarding (first-run wizard + demo seeder + health) ────────────────
  onboarding: {
    status: () => req<{ fresh: boolean; tenants: number; trunks: number; extensions: number; next_steps: { id: string; label: string; url: string }[] }>('GET', '/api/onboarding/status'),
    demo: () => req('POST', '/api/onboarding/demo', {}),
    removeDemo: () => req('DELETE', '/api/onboarding/demo'),
    health: () => req('GET', '/api/onboarding/health'),
  },

  // ── ring groups (per-tenant) ────────────────────────────────────────────
  ringGroups: {
    list: (tid: string) => req<any[]>('GET', `/api/tenants/${tid}/ring-groups`),
    create: (tid: string, body: any) => req('POST', `/api/tenants/${tid}/ring-groups`, body),
    update: (tid: string, id: string, body: any) => req('PUT', `/api/tenants/${tid}/ring-groups/${id}`, body),
    remove: (tid: string, id: string) => req('DELETE', `/api/tenants/${tid}/ring-groups/${id}`),
  },

  // ── boss-secretary profile ─────────────────────────────────────────────
  bossSecretary: {
    list: (params: { domain_uuid: string; boss_extension_uuid?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/boss-secretary?${qs}`);
    },
    get: (id: string) => req('GET', `/api/boss-secretary/${id}`),
    create: (body: any) => req('POST', '/api/boss-secretary', body),
    update: (id: string, body: any) => req('PUT', `/api/boss-secretary/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/boss-secretary/${id}`),
    toggle: (id: string) => req('POST', `/api/boss-secretary/${id}/toggle`),
    audit: (params: { profile_uuid?: string; since?: string; limit?: number }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/boss-secretary/audit/list?${qs}`);
    },
  },

  // ── embed keys (partner-site token mint gate for talk-v2) ───────────────
  embedKeys: {
    list: (link_uuid: string) => req<any[]>('GET', `/api/embed-keys?link_uuid=${link_uuid}`),
    create: (body: any) => req('POST', '/api/embed-keys', body),
    update: (id: string, body: any) => req('PUT', `/api/embed-keys/${id}`, body),
    revoke: (id: string) => req('POST', `/api/embed-keys/${id}/revoke`),
    remove: (id: string) => req('DELETE', `/api/embed-keys/${id}`),
  },

  // ── group geolocation (E911 / Kari's Law per-department address) ────────
  groupGeolocation: {
    list: (params: { domain_uuid: string; group_uuid?: string }) => {
      const qs = new URLSearchParams(params as any).toString();
      return req<any[]>('GET', `/api/group-geolocation?${qs}`);
    },
    get: (id: string) => req('GET', `/api/group-geolocation/${id}`),
    create: (body: any) => req('POST', '/api/group-geolocation', body),
    update: (id: string, body: any) => req('PUT', `/api/group-geolocation/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/group-geolocation/${id}`),
  },

  // ── system sub-pages ────────────────────────────────────────────────────
  system: {
    info:        () => req('GET', '/api/system/info'),
    options:     (d: string) => req('GET', `/api/system/options/${d}`),
    saveOptions: (d: string, body: any) => req('PUT', `/api/system/options/${d}`, body),
    dialCodes:   (d: string) => req('GET', `/api/system/dial-codes/${d}`),
    saveDialCodes: (d: string, body: any) => req('PUT', `/api/system/dial-codes/${d}`, body),
    parking:     (d: string) => req('GET', `/api/system/call-parking/${d}`),
    saveParking: (d: string, body: any) => req('PUT', `/api/system/call-parking/${d}`, body),
    parkingPool: () => req<{ base: number; count: number; timeout_seconds: number; timeouts: Record<string, number>; spots: { code: string; domain_uuid: string | null; department: string | null; label: string | null }[] }>('GET', '/api/system/parking-pool'),
    saveParkingPool: (body: any) => req('PUT', '/api/system/parking-pool', body),
    emergency:   (d: string) => req<any[]>('GET', `/api/system/emergency/${d}`),
    saveEmergency: (d: string, body: any) => req('PUT', `/api/system/emergency/${d}`, body),
    conferencing:(d: string) => req('GET', `/api/system/conferencing/${d}`),
    saveConferencing: (d: string, body: any) => req('PUT', `/api/system/conferencing/${d}`, body),
    email:       (d: string) => req('GET', `/api/system/email/${d}`),
    saveEmail:   (d: string, body: any) => req('PUT', `/api/system/email/${d}`, body),
    moh:         (d: string) => req('GET', `/api/system/music-on-hold/${d}`),
    saveMoh:     (d: string, body: any) => req('PUT', `/api/system/music-on-hold/${d}`, body),
    mohFiles:    () => req<any[]>('GET', '/api/system/moh-files'),
    deleteMohFile: (name: string) => req('DELETE', `/api/system/moh-files/${encodeURIComponent(name)}`),
    uploadMohFile: async (file: File) => {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch('/api/system/moh-files', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error(await r.text());
      return r.json() as Promise<{ ok: boolean; name: string; size: number }>;
    },
    generateMohFile: (body: { name: string; script?: string; voice?: string; music?: boolean; bed?: string }) =>
      req<{ ok: boolean; name: string; size: number; mode: 'speech' | 'music' }>('POST', '/api/system/moh-files/generate', body),
    // URL token unavoidable here: an <audio src> cannot send an Authorization header
    // (flagged for the server-side auth work -- short-lived media tokens).
    mohBlobUrl:  (name: string) =>`/api/system/moh-files-blob/${encodeURIComponent(name)}?token=${getToken()}`,
    // System default hold music (#1229) — fleet-wide fallback track.
    mohSystemDefault: () => req<{ present: boolean; size: number; mtime: string | null }>('GET', '/api/system/moh-system-default'),
    uploadMohSystemDefault: async (file: File) => {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch('/api/system/moh-system-default', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({} as any)))?.error || 'Upload failed');
      return r.json();
    },
    voicemail:   (d: string) => req('GET', `/api/system/voicemail/${d}`),
    saveVoicemail: (d: string, body: any) => req('PUT', `/api/system/voicemail/${d}`, body),
    alerts:      (d: string) => req('GET', `/api/system/alerts/${d}`),
    saveAlerts:  (d: string, body: any) => req('PUT', `/api/system/alerts/${d}`, body),
    // storage/saveStorage removed: /api/system/storage/:d never had a backend (portal-phones-ux).
    purge: (type: string, olderDays?: number) => req('POST', `/api/system/maintenance/purge/${type}`, { older_than_days: olderDays }),
  },

  // ── audit ───────────────────────────────────────────────────────────────
  audit: {
    list: (params: Record<string, string> = {}) => {
      const q = new URLSearchParams(params).toString();
      return req<{ rows: any[]; total: number }>('GET', `/api/audit${q ? '?' + q : ''}`);
    },
    // Header auth + blob download. The old window.open built `export.csv?a=b?token=...` (double
    // `?`), and /api/audit is not a query-token path anyway, so the export always 401'd.
    exportCsv: (params: Record<string, string> = {}) =>
      downloadWithAuth(withQuery('/api/audit/export.csv', params), `audit-${new Date().toISOString().slice(0, 10)}.csv`),
    verify: () => req('GET', '/api/audit/verify'),
  },

  // ── reports ─────────────────────────────────────────────────────────────
  reports: {
    callLog: (from: string, to: string) => req('GET', `/api/reports/old-call-log?from=${from}&to=${to}`),
    aiSummary: (from: string, to: string) => req('GET', `/api/reports/ai-summary?from=${from}&to=${to}`),
    sentimentTrends: (from: string, to: string) => req('GET', `/api/reports/sentiment-trends?from=${from}&to=${to}`),
    topics: (from: string, to: string) => req('GET', `/api/reports/topics?from=${from}&to=${to}`),
    callQuality: (from: string, to: string) => req('GET', `/api/reports/call-quality?from=${from}&to=${to}`),
    slaBreaches: (from: string, to: string, sla: number) => req('GET', `/api/reports/sla-breaches?from=${from}&to=${to}&sla_seconds=${sla}`),
    queueDetailed: (from: string, to: string) => req('GET', `/api/reports/queue-detailed?from=${from}&to=${to}`),
    /** Per-user Twilio cost attribution (#518). Returns { rows, note }. */
    twilioByUser: (from: string, to: string, domainUuid?: string) => {
      const p = new URLSearchParams({ from, to });
      if (domainUuid) p.set('domain_uuid', domainUuid);
      return req<{ rows: any[]; note: string }>('GET', `/api/reports/twilio-cost-by-user?${p}`);
    },
  },

  // ── voicemail / recordings ──────────────────────────────────────────────
  voicemail: {
    list: (d: string, ext?: string) => req<any[]>('GET', `/api/voicemail/list/${d}${ext ? `?ext=${ext}` : ''}`),
    transcribe: (callUuid: string) => req('POST', `/api/voicemail/${callUuid}/transcribe`),
    remove: (callUuid: string) => req('DELETE', `/api/voicemail/${callUuid}`),
    // Header auth + blob URL (replaces playUrl, which put the JWT in the link's ?token=).
    open:(callUuid: string) => openWithAuth(`/api/voicemail/play/${callUuid}`),
  },

  // ── wallboard ───────────────────────────────────────────────────────────
  wallboard: {
    snapshot: (d?: string) => req('GET', d ? `/api/wallboard/snapshot/${d}` : '/api/wallboard/snapshot'),
    queues:   (d?: string) => req('GET', d ? `/api/wallboard/queues/${d}` : '/api/wallboard/queues'),
  },

  // ── supervisor call monitoring (listen / whisper / barge) ─────────────────
  supervisor: {
    monitor: (target_uuid: string, mode: 'listen' | 'whisper' | 'barge', supervisor_extension?: string) =>
      req('POST', '/api/supervisor/monitor', { target_uuid, mode, supervisor_extension }),
  },

  // ── reseller ────────────────────────────────────────────────────────────
  reseller: {
    tenants: () => req<any[]>('GET', '/api/reseller/tenants'),
    usage: (d: string, from?: string, to?: string) => {
      const q = new URLSearchParams();
      if (from) q.set('from', from); if (to) q.set('to', to);
      return req('GET', `/api/reseller/tenants/${d}/usage${q.toString() ? '?' + q : ''}`);
    },
    billing: (month: string) => req('GET', `/api/reseller/billing?month=${month}`),
    setRatePlan: (d: string, plan: any) => req('PUT', `/api/reseller/tenants/${d}/rate-plan`, plan),
    health: () => req('GET', '/api/reseller/health'),
  },

  // ── billing (operator Square invoicing — #519 backend, #1050 UI) ─────────
  billing: {
    summary: () => req<BillingSummary>('GET', '/api/billing/summary'),
    seats: (domain_uuid: string) =>
      req<{ domain_uuid: string; desk_seats: number; mobile_seats: number; monthly_total: number }>(
        'GET', `/api/billing/seats?domain_uuid=${domain_uuid}`),
    invoices: (domain_uuid: string) => req<BillingInvoice[]>('GET', `/api/billing/invoices?domain_uuid=${domain_uuid}`),
    createInvoice: (body: { domain_uuid: string; period?: string; billing_email?: string; dry_run?: boolean }) =>
      req('POST', '/api/billing/invoice/create', body),
    settings: () =>
      req<{ token_configured: boolean; location_id: string | null; token_from_env: boolean;
            location_from_env: boolean; updated_at?: string; updated_by?: string }>(
        'GET', '/api/billing/settings'),
    saveSettings: (body: { access_token?: string; location_id?: string }) =>
      req('PUT', '/api/billing/settings', body),
  },

  // ── meet / talk ─────────────────────────────────────────────────────────
  meet: {
    list: () => req<any[]>('GET', '/api/meet'),
    create: (body: any) => req<{ meeting_uuid: string; room_id: string; jitsi_host: string }>('POST', '/api/meet', body),
    join: (roomId: string, name?: string, email?: string) => {
      const q = new URLSearchParams();
      if (name) q.set('name', name); if (email) q.set('email', email);
      return req<{ url: string; token: string }>('GET', `/api/meet/${roomId}/join${q.toString() ? '?' + q : ''}`);
    },
    end: (roomId: string) => req('DELETE', `/api/meet/${roomId}`),
  },

  // ── ai ──────────────────────────────────────────────────────────────────
  ai: {
    config: () => req('GET', '/api/ai/config'),
    saveConfig: (body: any) => req('PUT', '/api/ai/config', body),
    transcribe: (audioFile: File) => {
      const fd = new FormData();
      fd.append('audio', audioFile);
      return fetch('/api/ai/transcribe', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      }).then(r => r.json());
    },
  },

  // ── callflow ────────────────────────────────────────────────────────────
  callflow: {
    list:   () => req<any[]>('GET', '/api/callflow'),
    samples:() => req<any[]>('GET', '/api/callflow/samples'),
    get:    (id: string) => req('GET', `/api/callflow/${id}`),
    create: (body: any) => req('POST', '/api/callflow', body),
    update: (id: string, body: any) => req('PUT', `/api/callflow/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/callflow/${id}`),
  },

  // ── phone templates ─────────────────────────────────────────────────────
  templates: {
    list: () => req<any[]>('GET', '/api/templates'),
    get:  (id: string) => req('GET', `/api/templates/${id}`),
    create: (body: any) => req('POST', '/api/templates', body),
    update: (id: string, body: any) => req('PUT', `/api/templates/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/templates/${id}`),
  },

  // ── batch 6 endpoints ───────────────────────────────────────────────────
  fax: {
    list:   () => req<any[]>('GET', '/api/fax'),
    init:   () => req<{extension:string;auth_id:string;auth_password:string}>('GET', '/api/fax/init'),
    create: (body: any) => req('POST', '/api/fax', body),
    update: (id: string, body: any) => req('PUT', `/api/fax/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/fax/${id}`),
  },
  faxEmail: {
    settings: () => req<any>('GET', '/api/fax-email/settings'),
    save:     (body: any) => req<any>('PUT', '/api/fax-email/settings', body),
    test:     (body: any) => req<any>('POST', '/api/fax-email/test', body),
    log:      () => req<any[]>('GET', '/api/fax-email/log'),
  },
  fxs: {
    list:      () => req<any[]>('GET', '/api/fxs'),
    templates: () => req<any[]>('GET', '/api/fxs/templates'),
    create:    (body: any) => req('POST', '/api/fxs', body),
    remove:    (id: string) => req('DELETE', `/api/fxs/${id}`),
    updateLine:(id: string, line: number, body: any) => req('PUT', `/api/fxs/${id}/lines/${line}`, body),
  },
  inboundRules: {
    list:   () => req<any[]>('GET', '/api/inbound-rules'),
    create: (body: any) => req('POST', '/api/inbound-rules', body),
    update: (id: string, body: any) => req('PUT', `/api/inbound-rules/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/inbound-rules/${id}`),
    bulkDelete: (ids: string[]) => req<{ ok: boolean; deleted: number }>('POST', '/api/inbound-rules/bulk-delete', { ids }),
    move:   (id: string, direction: 'up' | 'down') => req('POST', `/api/inbound-rules/${id}/move`, { direction }),
  },
  holidays: {
    list:   () => req<any[]>('GET', '/api/holidays'),
    activeNow: () => req<any[]>('GET', '/api/holidays/active-now'),
    create: (body: any) => req('POST', '/api/holidays', body),
    update: (id: string, body: any) => req('PUT', `/api/holidays/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/holidays/${id}`),
  },
  emailTemplates: {
    list:   () => req<any[]>('GET', '/api/email-templates'),
    known:  () => req<string[]>('GET', '/api/email-templates/known'),
    save:   (body: any) => req('PUT', '/api/email-templates', body),
    preview:(body: any) => req('POST', '/api/email-templates/preview', body),
  },
  servicePrincipals: {
    list:   () => req<any[]>('GET', '/api/service-principals'),
    create: (body: any) => req<{principal_uuid:string;client_id:string;client_secret:string}>('POST', '/api/service-principals', body),
    regenerate: (id: string) => req<{client_id:string;client_secret:string}>('POST', `/api/service-principals/${id}/regenerate-secret`),
    remove: (id: string) => req('DELETE', `/api/service-principals/${id}`),
  },
  emergencyGeo: {
    list:    () => req<any[]>('GET', '/api/emergency-geo'),
    replace: (locations: any[]) => req('PUT', '/api/emergency-geo', locations),
    match:   (params: { extension?: string; ip?: string; mac?: string }) => {
      const q = new URLSearchParams(params as any).toString();
      return req('GET', `/api/emergency-geo/match?${q}`);
    },
  },
  trunkTemplates: {
    list:   () => req<{builtin:any[];custom:any[]}>('GET', '/api/trunk-templates'),
    builtin: (filename: string) => req<string>('GET', `/api/trunk-templates/builtin/${filename}`),
    custom: (filename: string) => req<string>('GET', `/api/trunk-templates/custom/${filename}`),
    apply:  (body: any) => req('POST', '/api/trunk-templates/apply', body),
    upload: (file: File) => {
      const fd = new FormData();
      fd.append('template', file);
      return fetch('/api/trunk-templates/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      }).then(r => r.json());
    },
    remove: (filename: string) => req('DELETE', `/api/trunk-templates/custom/${filename}`),
  },
  scheduledReports: {
    list:   () => req<any[]>('GET', '/api/scheduled-reports'),
    create: (body: any) => req('POST', '/api/scheduled-reports', body),
    update: (id: string, body: any) => req('PUT', `/api/scheduled-reports/${id}`, body),
    remove: (id: string) => req('DELETE', `/api/scheduled-reports/${id}`),
    runNow: (id: string) => req('POST', `/api/scheduled-reports/${id}/run-now`),
  },
  customPrompts: {
    list:   () => req<any[]>('GET', '/api/custom-prompts'),
    upload: (name: string, audio: File, description?: string) => {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('audio', audio);
      if (description) fd.append('description', description);
      return fetch('/api/custom-prompts', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      }).then(r => r.json());
    },
    remove: (id: string) => req('DELETE', `/api/custom-prompts/${id}`),
  },
  defs: () => req<any>('GET', '/api/defs'),
  parameters: {
    list:   () => req<any[]>('GET', '/api/parameters'),
    save:   (name: string, body: any) => req('PUT', `/api/parameters/${name}`, body),
    remove: (name: string) => req('DELETE', `/api/parameters/${name}`),
  },
  my: {
    user:   () => req('GET', '/api/my/user'),
    saveUser: (body: any) => req('PUT', '/api/my/user', body),
    group:  () => req('GET', '/api/my/group'),
    tokens: () => req<any[]>('GET', '/api/my/tokens'),
    createToken: (name: string) => req<{id:string;name:string;token:string}>('POST', '/api/my/tokens', { name }),
    removeToken: (id: string) => req('DELETE', `/api/my/tokens/${id}`),
  },

  // ── SMS / messaging (webclient Chat) ──────────────────────────────────────
  // Domain is inferred from the caller's JWT server-side; no domain_uuid needed
  // for a normal user. Config/conversations/messages are authed; the inbound
  // webhook is separate and unauthenticated (Twilio → /api/sms/webhook/:token).
  sms: {
    config: () => req<{ enabled: boolean; from_number: string | null; webhook_url: string }>('GET', '/api/sms/config'),
    conversations: () => req<any[]>('GET', '/api/sms/conversations'),
    messages: (uuid: string) => req<any[]>('GET', `/api/sms/conversations/${uuid}/messages`),
    markRead: (uuid: string) => req('POST', `/api/sms/conversations/${uuid}/read`, scopedBody()),
    // POSTs aren't auto-scoped (applyScope is GET-only), so carry the selected
    // department for an admin sending on behalf of another client. Non-admins
    // never have an active tenant → server pins to their own department.
    send: (to: string, body: string, media_urls?: string[]) =>
      req<{ ok: boolean; conversation_uuid: string; message_uuid: string; twilio_sid: string; status: string }>(
        'POST', '/api/sms/send', { to, body, ...(media_urls?.length ? { media_urls } : {}), ...scopedBody() }),
  },

  // ── Portal (webclient Calls / Voicemail) ──────────────────────────────────
  // Scope is inferred from the JWT server-side (own extension for a normal user).
  portal: {
    calls: (filter?: string, limit = 100) =>
      req<any[]>('GET', `/api/portal/calls?limit=${limit}${filter && filter !== 'all' ? `&filter=${filter}` : ''}`),
    voicemails: () => req<any[]>('GET', '/api/portal/voicemails'),
    vmHeard: (callUuid: string) => req('POST', `/api/portal/voicemail/${callUuid}/heard`),
    vmDelete: (callUuid: string) => req('DELETE', `/api/portal/voicemail/${callUuid}`),
    vmTranscript: (callUuid: string) => req<{ text: string }>('GET', `/api/portal/voicemail/${callUuid}/transcript`),
    directory: () => req<{ extension: string; name: string }[]>('GET', '/api/portal/directory'),
    settings: () => req<any>('GET', '/api/portal/me/settings'),
    saveForwarding: (body: any) => req('PUT', '/api/portal/me/forwarding', body),
    saveStatus: (body: { status?: string; do_not_disturb?: boolean }) => req('PUT', '/api/portal/me/status', body),
  },

  // ── my personal + shared contacts (scope inferred from JWT server-side) ───
  contacts: {
    list: (search = '', limit?: number) =>
      req<MyContact[]>('GET', `/api/me/contacts?search=${encodeURIComponent(search)}${limit ? `&limit=${limit}` : ''}`),
    create: (body: ContactInput) => req<MyContact>('POST', '/api/me/contacts', body),
    update: (id: string, body: Partial<ContactInput>) => req<{ ok: true }>('PUT', `/api/me/contacts/${id}`, body),
    remove: (id: string) => req<{ ok: true }>('DELETE', `/api/me/contacts/${id}`),
    groups: () => req<ContactGroup[]>('GET', '/api/me/contacts/groups'),
    shared: () => req<SharedContact[]>('GET', '/api/me/contacts/shared'),
    shares: (id: string) => req<ContactShare[]>('GET', `/api/me/contacts/${id}/shares`),
    share: (id: string, group_uuid: string) =>
      req<{ ok: true; shared: any }>('POST', `/api/me/contacts/${id}/share`, { group_uuid }),
    unshare: (id: string, group_uuid: string) =>
      req<{ ok: true }>('DELETE', `/api/me/contacts/${id}/share/${group_uuid}`),
  },
};

// Fetch an authed audio stream (recordings / voicemail) as an object URL —
// <audio src> can't send the Bearer header and these paths aren't in the
// query-token allowlist, so we fetch the blob with auth and hand back a URL.
// Caller must URL.revokeObjectURL when done.
export async function fetchAudioUrl(path: string): Promise<string> {
  const t = getToken();
  // applyScope appends ?domain_uuid=<active_tenant> so super_admin cross-tenant
  // audio requests hit the right domain instead of their own.
  const scopedPath = applyScope('GET', path);
  const r = await fetch(scopedPath, { headers: t ? { Authorization: `Bearer ${t}` } : {} });
  if (!r.ok) throw new Error('audio unavailable');
  return URL.createObjectURL(await r.blob());
}
